import { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MobileMenuComponent } from "components/Mobile/MenuComponent";
import { UserProfileViewContextProvider } from "context/userProfile/UserProfileViewContextProvider";
import { UserProfileViewContext } from "context/userProfile/UserProfileViewContext";
import { MobileDisplayNameSection } from "components/views/profile/Mobile/MobileDisplayNameSection";
import { MobileChangeEmailSection } from "components/views/profile/Mobile/MobileChangeEmailSection";
import { MobileLogingInSection } from "@components/views/profile/Mobile/MobileLogingInSection";
import { MobileOrganizationInfoSection } from "@components/views/profile/Mobile/MobileOrganizationInfoSection";
import { useAuthContext } from "hooks/useAuthContext";
import { RoutePaths } from "constants/RoutePaths";
import { MobileMenuButton } from "./MobileMenuButton";
import { MobileHeader1, MobileHeader2 } from "@theme/styledComponents";
import { styledConstants } from "@theme/styledConstants";
import TopMenuOffset from "@components/Mobile/TopMenuOffsetComponent";

const StyledDivider = styled(Divider)({
  marginTop: "-0.2rem"
});

const LoadingBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const PageBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  position: "relative",
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(5),
  gap: theme.spacing(2),
  overflow: "hidden",
  backgroundColor: theme.palette.common.white,
}));

const StyledSubtitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  color: theme.palette.text.secondary,
  fontSize: "0.85rem",
}));

const StyledDescriptionTypography = styled(Typography)({
  fontSize: "0.8rem",
});

const MobileProfilePageContent = () => {
  const { user, getProfilePending, isProfileOfLoggedUser } = useContext(
    UserProfileViewContext,
  );
  const { userState } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("userId");

  useEffect(() => {
    if (userState?.user?.userId && userId && userState.user.userId === userId) {
      navigate(RoutePaths.PROFILE);
    }
  }, [userState?.user?.userId, userId, navigate]);

  if (getProfilePending || !user) {
    return (
      <LoadingBox>
        <CircularProgress />
      </LoadingBox>
    );
  }

  return (
    <PageBox>   
     <MobileMenuButton onClick={() => setMenuOpen(true)} />
      <MobileMenuComponent open={menuOpen} onClose={() => setMenuOpen(false)} />
        <TopMenuOffset />
      <Stack spacing={2}>
        <Box>
          <MobileHeader1>
            {t("profile.profile")}
          </MobileHeader1>
          <StyledSubtitleTypography variant="body1">
            {t("profile.windowDescription")}
          </StyledSubtitleTypography>
        </Box>
        <Box>
          <StyledDescriptionTypography variant="body1">
            {t("profile.displayNameDescription")}
          </StyledDescriptionTypography>
        </Box>
      </Stack>
      <StyledDivider />
      <Stack gap={2} mt={styledConstants.Header2TopMargin}>
        <MobileHeader2>
          {t("profile.yourData")}
        </MobileHeader2>
        <MobileDisplayNameSection
          user={user}
          isProfileOfLoggedUser={isProfileOfLoggedUser}
        />
        <MobileChangeEmailSection email={user.email} />
      </Stack>
      <StyledDivider />
      <MobileLogingInSection user={user} />
      <StyledDivider />
      <MobileOrganizationInfoSection
        organizationName={user.workspace.name}
        patronNames={user.patrons.map((p) => p.displayName)}
        userRole={user.role}
        tagA={user.tagA || ""}
        tagB={user.tagB || ""}
      />
    </PageBox>
  );
};

export const UserProfileViewMobile = () => (
  <UserProfileViewContextProvider>
    <MobileProfilePageContent />
  </UserProfileViewContextProvider>
);
